import { isEmpty } from '@sgme/fp';
import classNames from 'classnames';
import { useToProductTypeLabel } from 'components/common-form/builder/form/node/field/ProductListField';
import { LearnMoreIcon } from 'components/shared/LearnMore/LearnMoreIcon';
import type { IntlKey } from 'locales';
import { sortBy } from 'lodash-es';
import type { ProductVariation } from 'models/productsReferential';
import { FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import type { AppState } from 'state/state.model';
import type { WorkspaceWithProductSelector } from 'state/workspaces/workspaces/workspaces.model';
import { getActivatedAssetTypeFilter } from 'state/workspaces/workspaces/workspaces.selector';
import { optionalMap } from 'utils/optional';

import { ProductCategoryItem } from './ProductCategoryItem';

interface ProductSubTypeSelectorProps {
  className: string;
  workspace: WorkspaceWithProductSelector;
  availableProductVariations: readonly ProductVariation[];
  selectedProductType?: string;
  selectedProductSubType?: string;
  onProductSubTypeSelected(productSubType: string): void;
}

export const ProductSubTypeSelector: FC<ProductSubTypeSelectorProps> = ({
  className,
  workspace,
  availableProductVariations,
  selectedProductType,
  selectedProductSubType,
  onProductSubTypeSelected,
}) => {
  const activatedAssetTypeFilter = useSelector((state: AppState) => getActivatedAssetTypeFilter(state, workspace));

  const productSubTypeOptions = useGetProductSubTypeOptions(
    availableProductVariations,
    activatedAssetTypeFilter,
    selectedProductType,
  );

  return (
    <div data-e2e="product-subtype-selector" className={classNames('border-end overflow-auto', className)}>
      {!isEmpty(productSubTypeOptions) ? (
        productSubTypeOptions.map(({ label, value }) => (
          <ProductCategoryItem
            key={value}
            e2e="product-subtype-selector-item"
            className="ps-3 pe-0"
            isItemSelected={selectedProductSubType === value}
            onItemClick={() => onProductSubTypeSelected(value)}
          >
            <span className="text-wrap text-start me-auto">{label}</span>
            {workspace === 'static' && <LearnMoreIcon productType={selectedProductType} productSubType={value} />}
            <span className="icon icon-sm text-muted mx-2">keyboard_arrow_right</span>
          </ProductCategoryItem>
        ))
      ) : (
        <div className="h-100 d-flex flex-column justify-content-center align-items-center px-4">
          <span className="icon icon-outlined icon-lg text-secondary">group_work</span>
          <p className="text-center text-secondary">
            <FormattedMessage id="productSelector.available.productSubtypes" />
          </p>
        </div>
      )}
    </div>
  );
};

const useGetProductSubTypeOptions = (
  productVariations: readonly ProductVariation[],
  activatedAssetTypeFilter: string,
  productType?: string,
) => {
  const { formatMessage } = useIntl();
  const toProductTypeLabel = useToProductTypeLabel();
  const toLabel = optionalMap(toProductTypeLabel);

  return useMemo(() => {
    if (productType === undefined) {
      return [];
    }

    const filteredProductSubTypes = productVariations.filter(
      product =>
        product.productType === productType &&
        (product.assetTypes?.some(assetType => activatedAssetTypeFilter === assetType) ||
          activatedAssetTypeFilter === 'All'),
    );

    const distinctProductSubTypeOptions = filteredProductSubTypes
      .filter(
        ({ productSubType }, pos, all) => all.findIndex(product => product.productSubType === productSubType) === pos,
      )
      .map(({ productSubType, commercialProductSubType }) => ({
        label:
          toLabel(commercialProductSubType) ??
          formatMessage({
            id: `quote.products.${productSubType}` as IntlKey,
            defaultMessage: productSubType,
          }),
        value: productSubType,
      }));

    return sortBy(distinctProductSubTypeOptions, o => o.label);
  }, [activatedAssetTypeFilter, formatMessage, productType, productVariations, toLabel]);
};
